import RateMovement from '@/components/RateMovement';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { getCurrentRates, getMetalHistory, calculatePrice } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TrendingUp, TrendingDown, Calculator, Activity, Zap } from 'lucide-react';
import { formatInr, MONEY_CLASSNAME } from '@/lib/money';

const PERIODS = ['1D', '1W', '1M', '1Y'];

const formatRateAge = (timestamp) => {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000));
  if (minutes < 1) return 'Updated just now';
  if (minutes < 60) return `Updated ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  return `Updated ${hours} hour${hours === 1 ? '' : 's'} ago`;
};

const formatPublication = (rate) => {
  const day = rate.source_date ? new Date(`${rate.source_date}T00:00:00+05:30`) : new Date(rate.timestamp);
  const published = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric',
  }).format(day);
  return `IBJA ${rate.session || ''} benchmark • ${published}`;
};

export default function MetalRatesPage() {
  const [rates, setRates] = useState(null);
  const [selectedMetal, setSelectedMetal] = useState('gold');
  const [period, setPeriod] = useState('1M');
  const [history, setHistory] = useState([]);
  const [calcMetal, setCalcMetal] = useState('gold');
  const [calcWeight, setCalcWeight] = useState('1');
  const [calcPurity, setCalcPurity] = useState('999');
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rateUnavailable, setRateUnavailable] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getCurrentRates();
        setRates(res.data);
        setRateUnavailable(false);
      } catch (err) {
        setRates(null);
        setRateUnavailable(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await getMetalHistory(selectedMetal, period);
      const data = res.data.map(r => ({
        time: new Date(r.timestamp).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
        price: r.rate_per_gram,
      }));
      setHistory(data);
    } catch (err) {
      console.error(err);
    }
  }, [selectedMetal, period]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleCalc = async () => {
    if (!rates || !calcWeight || parseFloat(calcWeight) <= 0) return;
    setCalcLoading(true);
    try {
      const res = await calculatePrice({ metal_type: calcMetal, weight: parseFloat(calcWeight), purity_fineness: parseInt(calcPurity) });
      setCalcResult(res.data);
    } catch (err) {
      setCalcResult(null);
      setRateUnavailable(true);
    } finally {
      setCalcLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const data = payload[0].payload;
    const priceChange = payload.length > 1 ? payload[0].value - (history[0]?.price || 0) : 0;
    return (
      <div className="bg-card/95 border border-border backdrop-blur-md p-4 rounded-xl shadow-xl">
        <p className="text-xs text-muted-foreground mb-1">{data.time}</p>
        <p className={`${MONEY_CLASSNAME} text-xl font-semibold text-primary`}>{formatInr(payload[0].value)}</p>
        <p className="text-[11px] text-muted-foreground">per gram</p>
      </div>
    );
  };

  // Calculate min/max for better chart scaling
  const priceMin = history.length > 0 ? Math.min(...history.map(h => h.price)) * 0.995 : 0;
  const priceMax = history.length > 0 ? Math.max(...history.map(h => h.price)) * 1.005 : 0;
  const priceChange = history.length > 1 ? history[history.length - 1]?.price - history[0]?.price : 0;
  const priceChangePercent = history.length > 1 && history[0]?.price ? ((priceChange / history[0].price) * 100).toFixed(2) : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="metal-rates-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-6 h-6 text-primary" />
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight uppercase">Live Rate</h1>
          </div>
          <p className="text-muted-foreground mb-8">Published gold and silver rates with historical pricing</p>
        </motion.div>

        {/* Current Rates */}
        {!loading && rateUnavailable && (
          <div className="mb-8 p-4 border border-amber-500/30 bg-amber-500/10 text-amber-400 rounded-lg" data-testid="rates-unavailable-message">
            Live rate temporarily unavailable
          </div>
        )}
        {rates && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
            <motion.div
              className="p-6 bg-card border border-primary/20 rounded-xl cursor-pointer transition-all duration-300 hover:border-primary/50"
              onClick={() => setSelectedMetal('gold')}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="gold-rate-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">Gold (24K)</p>
                    {rates.gold.source === 'ibja_manual' && (
                      <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-xl font-mono uppercase">Live</span>
                    )}
                  </div>
                  <p className={`${MONEY_CLASSNAME} text-4xl font-semibold text-primary`}>
                    {formatInr(rates.gold.rate_per_gram)}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <p className="text-sm text-muted-foreground">per gram</p>
                    <RateMovement rate={rates.gold} />
                  </div>
                  <p className="text-xs text-muted-foreground/60 mt-1" data-testid="gold-rate-updated">{formatPublication(rates.gold)} · {formatRateAge(rates.gold.checked_at || rates.gold.timestamp)}</p>
                  {rates.gold.high && rates.gold.low && (
                    <p className="text-xs text-muted-foreground/60 mt-1 font-mono">
                      H: {formatInr(rates.gold.high)} / L: {formatInr(rates.gold.low)}
                    </p>
                  )}
                </div>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-3xl text-primary font-display font-bold">Au</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="p-6 bg-card border border-silver/20 rounded-xl cursor-pointer transition-all duration-300 hover:border-silver/50"
              onClick={() => setSelectedMetal('silver')}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              data-testid="silver-rate-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-widest">Silver (999)</p>
                    {rates.silver.source === 'ibja_manual' && (
                      <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-xl font-mono uppercase">Live</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground/60 mt-1" data-testid="silver-rate-updated">{formatPublication(rates.silver)} · {formatRateAge(rates.silver.checked_at || rates.silver.timestamp)}</p>
                  <p className={`${MONEY_CLASSNAME} text-4xl font-semibold text-silver`}>
                    {formatInr(rates.silver.rate_per_gram)}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <p className="text-sm text-muted-foreground">per gram</p>
                    <RateMovement rate={rates.silver} />
                  </div>
                  {rates.silver.high && rates.silver.low && (
                    <p className="text-xs text-muted-foreground/60 mt-1 font-mono">
                      H: {formatInr(rates.silver.high)} / L: {formatInr(rates.silver.low)}
                    </p>
                  )}
                </div>
                <div className="w-16 h-16 bg-silver/10 rounded-full flex items-center justify-center">
                  <span className="text-3xl text-silver font-display font-bold">Ag</span>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Chart */}
        <div className="mb-12" data-testid="price-chart">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="font-display text-2xl font-bold flex items-center gap-2">
                <Activity className={`w-5 h-5 ${selectedMetal === 'gold' ? 'text-primary' : 'text-silver'}`} />
                {selectedMetal === 'gold' ? 'Gold' : 'Silver'} Price History
              </h2>
              {history.length > 1 && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">{period} Change:</span>
                  <span className={`text-sm font-mono font-semibold flex items-center gap-1 ${priceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {priceChange >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    {priceChange >= 0 ? '+' : '-'}{formatInr(Math.abs(priceChange))} ({priceChangePercent}%)
                  </span>
                </div>
              )}
            </div>
            <Tabs value={period} onValueChange={setPeriod}>
              <TabsList className="bg-secondary border border-border/40">
                {PERIODS.map(p => (
                  <TabsTrigger key={p} value={p} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs font-mono" data-testid={`period-${p}`}>
                    {p}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          <div className="bg-card border border-border/40 rounded-xl p-6 relative overflow-hidden">
            {/* Subtle gradient background */}
            <div className={`absolute inset-0 opacity-5 ${selectedMetal === 'gold' ? 'bg-gradient-to-br from-primary via-transparent to-transparent' : 'bg-gradient-to-br from-silver via-transparent to-transparent'}`} />
            
            <div className="h-80 relative">
              {history.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.4} />
                        <stop offset="50%" stopColor="#D4AF37" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="silverGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#E0E0E0" stopOpacity={0.4} />
                        <stop offset="50%" stopColor="#E0E0E0" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="#E0E0E0" stopOpacity={0} />
                      </linearGradient>
                      <filter id="glow">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis
                      dataKey="time"
                      stroke="#333"
                      tick={{ fill: '#666', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                      axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                      tickLine={false}
                      interval="preserveStartEnd"
                      dy={10}
                    />
                    <YAxis
                      stroke="#333"
                      tick={{ fill: '#666', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                      axisLine={false}
                      tickLine={false}
                      domain={[priceMin, priceMax]}
                      tickFormatter={(v) => formatInr(v)}
                      width={70}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }} />
                    <Area
                      type="monotone"
                      dataKey="price"
                      stroke={selectedMetal === 'gold' ? '#D4AF37' : '#E0E0E0'}
                      strokeWidth={2.5}
                      fill={selectedMetal === 'gold' ? 'url(#goldGrad)' : 'url(#silverGrad)'}
                      filter="url(#glow)"
                      dot={false}
                      activeDot={{ r: 6, strokeWidth: 2, stroke: selectedMetal === 'gold' ? '#D4AF37' : '#E0E0E0', fill: '#0a0a0a' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs text-muted-foreground">Loading chart data...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Live indicator */}
            {rates && (
              <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-border/30">
                <div className="flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5 text-green-400" />
                  <span className="text-xs text-muted-foreground">Live Rate</span>
                </div>
                <span className="text-xs text-muted-foreground">•</span>
              </div>
            )}
          </div>
        </div>

        <Separator className="bg-white/10 mb-12" />

        {/* Price Calculator */}
        <div data-testid="price-calculator">
          <div className="flex items-center gap-3 mb-6">
            <Calculator className="w-5 h-5 text-primary" />
            <h2 className="font-display text-2xl font-bold">Price Calculator</h2>
          </div>
          <div className="bg-card border border-border/40 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest mb-2 block">Metal</label>
                <Select value={calcMetal} onValueChange={(v) => { setCalcMetal(v); setCalcPurity('999'); }}>
                  <SelectTrigger className="bg-card border-border h-12" data-testid="calc-metal-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="gold">Gold</SelectItem>
                    <SelectItem value="silver">Silver</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest mb-2 block">BIS Purity</label>
                <Select value={calcPurity} onValueChange={setCalcPurity}>
                  <SelectTrigger className="bg-card border-border h-12" data-testid="calc-purity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {calcMetal === 'gold' ? (
                      <>
                        <SelectItem value="999">24K (999) - 99.9%</SelectItem>
                        <SelectItem value="916">22K (916) - 91.6%</SelectItem>
                        <SelectItem value="750">18K (750) - 75%</SelectItem>
                        <SelectItem value="585">14K (585) - 58.5%</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="999">Fine (999) - 99.9%</SelectItem>
                        <SelectItem value="925">Sterling (925) - 92.5%</SelectItem>
                        <SelectItem value="900">Coin (900) - 90%</SelectItem>
                        <SelectItem value="835">European (835) - 83.5%</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest mb-2 block">Weight (grams)</label>
                <Input
                  type="number"
                  value={calcWeight}
                  onChange={(e) => setCalcWeight(e.target.value)}
                  className="bg-card border-border h-12"
                  min="0.1"
                  step="0.1"
                  data-testid="calc-weight-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleCalc}
                  disabled={calcLoading || !rates}
                  className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-wide rounded-xl h-12"
                  data-testid="calc-submit-button"
                >
                  {calcLoading ? 'Calculating...' : rates ? 'Calculate' : 'Live Rate Unavailable'}
                </Button>
              </div>
            </div>

            {calcResult && (
              <motion.div
                className="mt-6 space-y-2 border-t border-border/40 pt-6 text-sm"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="calc-result"
              >
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Estimated Supplier Cost</span>
                  <span className={MONEY_CLASSNAME}>{formatInr(calcResult.supplier_cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">SŌHM Margin ({calcResult.margin_percent}%)</span>
                  <span className={MONEY_CLASSNAME}>{formatInr(calcResult.sohm_margin)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">GST ({calcResult.gst_percent}%)</span>
                  <span className={MONEY_CLASSNAME}>{formatInr(calcResult.gst)}</span>
                </div>
                <Separator className="bg-white/10 !my-2" />
                <div className="flex justify-between font-bold text-lg">
                  <span className="text-primary">Estimated Total</span>
                  <span className={`${MONEY_CLASSNAME} text-primary`}>{formatInr(calcResult.total_price)}</span>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
